<template>
  <div>
    <helpers-section-header class="mt-6 mb-8"> 13D/G Filing Signal Rules </helpers-section-header>

    <helpers-section-sub-header class="mt-6 mb-2"> Signal Evaluation: </helpers-section-sub-header>
    <v-row>
      <v-col v-if="!appcriteria.general.screener" cols="12">
        <v-text-field
          v-model="criteria.number_of_days_to_evaluate"
          label="Days to evaluate"
          suffix="days"
          variant="outlined"
          density="compact"
          hint="Number of days after filing released (plus trading delay) to continue to evaluate as a buy.  max allowed is 100"
          persistent-hint
          style="max-width: 250px"
        />
      </v-col>
    </v-row>

    <!-- Filing Types -->
    <helpers-section-sub-header class="mt-8 mb-2"> Filing Types </helpers-section-sub-header>
    <v-row>
      <v-col cols="12">
        <v-checkbox
          v-model="criteria.include_13d"
          label="Include 13D Filings"
          hide-details
          class="mt-0"
          density="compact"
        />
        <v-checkbox
          v-model="criteria.include_13g"
          label="Include 13G Filings"
          hide-details
          density="compact"
        />
      </v-col>
    </v-row>

    <!-- Triggers -->
    <helpers-section-sub-header class="mt-6 mb-2"> Triggers </helpers-section-sub-header>
    <v-row>
      <v-col cols="12">
        <v-text-field
          v-model="criteria.percent_ownership_threshold"
          label="Percent ownership"
          suffix="%"
          variant="outlined"
          density="compact"
          hint="Treat filings where the ownership exceeds the percentage as a buy signal"
          persistent-hint
          style="max-width: 250px"
        />
        <v-checkbox
          v-model="criteria.treat_amended_as_buys"
          density="compact"
          label="Also treat amended filings as a buy signal"
          hide-details
        />
        <v-checkbox
          v-model="criteria.amended_must_increase"
          density="compact"
          label="Require amended filings show additional accumulation before being treated as a buy signal"
          hide-details
        />
        <v-checkbox
          v-if="1 == 2"
          v-model="criteria.treat_below_5_as_short"
          density="compact"
          label="Treat filings where the % ownership falls below the 5% minimum as a short signal"
          hide-details
        />
        <p>Exclude stocks if filer's ownership is greater than:</p>
        <v-text-field
          v-model="criteria.exclude_ownership_over"
          style="max-width: 300px"
          variant="outlined"
          density="compact"
          label="exclude stocks with % ownership >"
        />
      </v-col>
    </v-row>

    <!-- Trading Delay -->
    <helpers-section-sub-header class="mt-6 mb-2"> Trading delay </helpers-section-sub-header>
    <v-row>
      <v-col cols="12">
        <v-text-field
          v-model="criteria.trading_delay"
          type="number"
          suffix="days"
          variant="outlined"
          density="compact"
          label="Days since release"
          hint="Days after filing(s) released before being considered for backtesting"
          persistent-hint
          style="max-width: 250px"
        />
      </v-col>
    </v-row>

    <!-- Keywords -->
    <helpers-section-sub-header class="mt-6 mb-2"> Keywords </helpers-section-sub-header>
    <v-row>
      <v-col cols="12">
        <v-checkbox
          v-model="criteria.long_keywords_enabled"
          label="Buy Signal: Must include keywords in filing reason for transaction"
          hide-details
          density="compact"
        />
        <v-text-field
          v-model="criteria.long_keywords"
          variant="outlined"
          density="compact"
          :disabled="!criteria.long_keywords_enabled"
          placeholder="Enter keywords separated by space or comma"
        />
        <v-checkbox
          v-model="criteria.short_keywords_enabled"
          label="Sell Signal: Must include keywords in filing reason for transaction"
          hide-details
          density="compact"
        />
        <v-text-field
          v-model="criteria.short_keywords"
          variant="outlined"
          density="compact"
          :disabled="!criteria.short_keywords_enabled"
          placeholder="Enter keywords separated by space or comma"
          hide-details
        />
      </v-col>
    </v-row>

    <!-- dynamic funds -->
    <helpers-section-sub-header class="mt-6 mb-2">
      Dynamic Fund Selection (optional)
    </helpers-section-sub-header>
    <v-row>
      <v-col cols="12" sm="12" md="6">
        <helpers-section-sub-header
          >AUM of Fund's 13F Portfolio (leave blank for any)</helpers-section-sub-header
        >
        <v-row density="compact">
          <v-col v-for="item in criteria.f13_aums" :key="item.id" cols="12">
            <v-switch
              v-model="item.selected"
              :label="item.name"
              color="primary"
              density="compact"
              hide-details
              title="Click to toggle filter"
              class="mt-0 ml-2"
            />
          </v-col>
        </v-row>
        <v-checkbox
          v-model="criteria.allow_empty_13f"
          density="compact"
          label="Allow Funds With No 13F Filings"
          hide-details
        />
      </v-col>
      <v-col cols="12" sm="12" md="4">
        <v-row density="compact">
          <v-col cols="12">
            <v-text-field
              v-model="criteria.minimum_prior_1m_perf"
              density="compact"
              variant="outlined"
              hide-details
              label="minimum prior 1 month perf history"
              hint="only uses history known prior to that date"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="criteria.minimum_prior_6m_perf"
              variant="outlined"
              density="compact"
              hide-details
              label="minimum prior 6 month perf history"
              hint="only uses history known prior to that date"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="criteria.minimum_prior_filings"
              variant="outlined"
              density="compact"
              hide-details
              label="minimum prior 13D/G filings"
              hint="only uses history known prior to that date"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Fund Group Manager -->
    <helpers-section-header class="mt-6 mb-2">
      Specific Fund selection (optional)
    </helpers-section-header>
    <core-fund-group-manager
      :filer-group="criteria.filer_group"
      flat
      no-padding
      @update:filer-group="
        newVal => {
          criteria.filer_group.id = newVal.id
          criteria.filer_group.filers = newVal.filers
        }
      "
    />
  </div>
</template>

<script>
// Modules
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import { mapWritableState } from 'pinia'
import cloneDeep from 'lodash/cloneDeep'
export default {
  data() {
    return {
      dialog: false,
      f13_aums: [
        { id: 1, name: 'Micro (less than $250M)', selected: false },
        { id: 2, name: 'Small (less than $2B)', selected: false },
        { id: 3, name: 'Mid (less than $10B)', selected: false },
        { id: 4, name: 'Large (less than $250B)', selected: false },
        { id: 5, name: 'Mega (greater than $250B)', selected: false }
      ]
    }
  },
  computed: {
    ...mapWritableState(useAppStore, ['whaleTraderCriteria']),
    criteria() {
      return this.whaleTraderCriteria.signals.d13_signal
    },
    appcriteria() {
      return this.whaleTraderCriteria
    },
    enabled() {
      return this.whaleTraderCriteria.signals.d13_signal.enabled
    }

    /*...mapFields({
      appcriteria: 'app.whaleTraderCriteria',
      criteria: 'app.whaleTraderCriteria.signals.d13_signal',
      enabled: 'app.whaleTraderCriteria.signals.d13_signal.enabled',
      signalView: 'app.signalView'
    })*/
  },
  watch: {
    criteria: {
      deep: true,
      immediate: true,

      handler(newObj, oldObj) {
        if (!this.criteria.f13_aums || this.criteria.f13_aums.length == 0) {
          this.criteria.f13_aums = []
          for (var i = 0; i < this.f13_aums.length; i++) {
            var f13 = this.f13_aums[i]
            this.criteria.f13_aums.push(cloneDeep(f13))
          }
        }
      }
    }
  }
}
</script>
